import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, RefreshCw, Calendar, ChevronLeft, ChevronRight, LogOut, FolderPlus, Settings } from 'lucide-react';
import { format, subMonths, addMonths } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import ExpensesByCategoryChart from './components/ExpensesByCategoryChart';
import ExpensesByCategoryBreakdown from './components/ExpensesByCategoryBreakdown';
import ExpenseList from './components/ExpenseList';
import MonthlyExpensesChart from './components/MonthlyExpensesChart';
import CategoryChartsGrid from './components/CategoryChartsGrid';
import UnifiedCreateModal from './components/UnifiedCreateModal';
import AccountSettingsModal from './components/AccountSettingsModal';
import CurrencySelect from './components/CurrencySelect';
import EditExpenseModal from './components/EditExpenseModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { PieChartSkeleton, CategoryListSkeleton } from './components/SkeletonLoaders';
import apiService from './services/apiService';
import { Expense, ExpenseCreateRequest, ExpenseUpdateRequest } from './types';
import { getCurrencyDisplay, CURRENCIES } from './config/currencies';
import { useCategories, useExpenses, useCategoryExpenses } from './hooks';
import { useToast } from './hooks/useToast';
import { useAuth } from './contexts/AuthContext';
import './styles/globals.css';

function Dashboard() {
  const { showError } = useToast();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  // UI State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createModalMode, setCreateModalMode] = useState<'expense' | 'category'>('expense');
  const [creatingExpense, setCreatingExpense] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState<Expense | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [currencies] = useState<string[]>(Object.keys(CURRENCIES));
  const [currency, setCurrency] = useState(user?.default_currency || 'USD');

  // Update currency when user default changes (only on initial load)
  useEffect(() => {
    if (user?.default_currency) {
      setCurrency(user.default_currency);
    }
  }, [user?.default_currency]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempMonth, setTempMonth] = useState(selectedDate.getMonth());
  const [tempYear, setTempYear] = useState(selectedDate.getFullYear());

  // Monthly chart date range - independent state (null = all-time)
  const [monthlyChartStartDate, setMonthlyChartStartDate] = useState<Date | null>(null);
  const [monthlyChartEndDate, setMonthlyChartEndDate] = useState<Date | null>(null);

  // Category trends date range - independent state (null = all-time)
  const [categoryTrendsStartDate, setCategoryTrendsStartDate] = useState<Date | null>(null);
  const [categoryTrendsEndDate, setCategoryTrendsEndDate] = useState<Date | null>(null);

  // Key to force refresh of charts
  const [chartsRefreshKey, setChartsRefreshKey] = useState(0);

  const categoryWheelRef = useRef<HTMLDivElement>(null);
  const monthlyExpensesRef = useRef<HTMLDivElement>(null);
  const expensesListRef = useRef<HTMLDivElement>(null);

  // Data hooks
  const { categories, loading: categoriesLoading, reload: reloadCategories } = useCategories();
  const { expenses, warnings, loading: expensesLoading, reload: reloadExpenses } = useExpenses(currency, selectedDate);
  const { categoryExpenses, loading: categoryExpensesLoading, reload: reloadCategoryExpenses } = useCategoryExpenses(currency, selectedDate);

  // Reload all data after mutations
  const reloadAllData = async () => {
    await Promise.all([
      reloadExpenses(),
      reloadCategoryExpenses()
    ]);
    // Force refresh of monthly and category charts
    setChartsRefreshKey(prev => prev + 1);
  };

  // Sync heights between left and right columns
  useEffect(() => {
    const syncHeights = () => {
      if (categoryWheelRef.current && monthlyExpensesRef.current) {
        const leftHeight = categoryWheelRef.current.offsetHeight;
        monthlyExpensesRef.current.style.height = `${leftHeight}px`;
      }
    };

    syncHeights();
    window.addEventListener('resize', syncHeights);

    return () => window.removeEventListener('resize', syncHeights);
  }, [categoryExpenses, expenses]);

  const handleCreateExpense = async (expense: ExpenseCreateRequest) => {
    try {
      setCreatingExpense(true);
      await apiService.createExpense(expense);
      setIsCreateModalOpen(false);
      await reloadAllData();
    } catch (error) {
      console.error('Error creating expense:', error);
      showError('Failed to create expense. Please try again.');
    } finally {
      setCreatingExpense(false);
    }
  };

  const handleEditExpense = async (expense: Expense) => {
    try {
      // Fetch the expense without conversion to get original data from DB
      const originalExpense = await apiService.getExpenseForEdit(expense.id);
      setEditingExpense(originalExpense);
      setIsEditModalOpen(true);
    } catch (error) {
      console.error('Error fetching expense for edit:', error);
      showError('Failed to load expense details. Please try again.');
    }
  };

  const handleUpdateExpense = async (updatedExpense: Expense) => {
    try {
      setCreatingExpense(true);
      const updateRequest: ExpenseUpdateRequest = {
        name: updatedExpense.name,
        amount: updatedExpense.amount,
        currency: updatedExpense.currency,
        description: updatedExpense.description,
        category_id: updatedExpense.category?.id,
        created_at: updatedExpense.created_at,
        is_big_expense: updatedExpense.is_big_expense
      };

      await apiService.updateExpense(updatedExpense.id, updateRequest, currency);
      setIsEditModalOpen(false);
      setEditingExpense(null);
      await reloadAllData();
    } catch (error) {
      console.error('Error updating expense:', error);
      showError('Failed to update expense. Please try again.');
    } finally {
      setCreatingExpense(false);
    }
  };

  const handleDeleteExpense = (id: number) => {
    const expenseToDelete = expenses.find(expense => expense.id === id);
    if (expenseToDelete) {
      setDeletingExpense(expenseToDelete);
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingExpense) return;

    try {
      setIsDeleting(true);
      await apiService.deleteExpense(deletingExpense.id);
      setIsDeleteModalOpen(false);
      setDeletingExpense(null);
      await reloadAllData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      showError('Failed to delete expense. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeletingExpense(null);
  };

  const handleCreateCategory = async (name: string, parentId: number | null) => {
    try {
      setCreatingCategory(true);
      await apiService.createCategory(name, parentId);
      setIsCreateModalOpen(false);
      await reloadCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      showError('Failed to create category. Please try again.');
      throw error; // Re-throw to let modal handle it
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleRefresh = async () => {
    // Reload all data including charts
    await reloadAllData();

    // Also refresh date range in case new expenses are outside current range
    try {
      const dateRange = await apiService.getExpenseDateRange();
      if (dateRange.has_expenses && dateRange.first_expense_date && dateRange.last_expense_date) {
        setMonthlyChartStartDate(new Date(dateRange.first_expense_date));
        setMonthlyChartEndDate(new Date(dateRange.last_expense_date));
      }
    } catch (error) {
      console.error('Error refreshing date range:', error);
    }
  };

  const handleMonthlyChartDateRangeChange = (startDate: Date, endDate: Date) => {
    setMonthlyChartStartDate(startDate);
    setMonthlyChartEndDate(endDate);
  };

  const handleCategoryTrendsDateRangeChange = (startDate: Date, endDate: Date) => {
    setCategoryTrendsStartDate(startDate);
    setCategoryTrendsEndDate(endDate);
  };

  // Function to sync heights between category container and expenses list
  const syncContainerHeights = useCallback(() => {
    if (categoryWheelRef.current && expensesListRef.current) {
      const categoryHeight = categoryWheelRef.current.offsetHeight;
      const expensesListHeader = document.querySelector('.expense-list-header');
      const headerHeight = expensesListHeader ? expensesListHeader.clientHeight : 0;
      const padding = 32; // Account for container padding

      const availableHeight = categoryHeight - headerHeight - padding;
      expensesListRef.current.style.maxHeight = `${Math.max(availableHeight, 200)}px`;
    }
  }, []);

  // Sync heights after data loads and layout changes
  useEffect(() => {
    const timer = setTimeout(() => {
      syncContainerHeights();
    }, 100); // Small delay to ensure DOM is updated

    return () => clearTimeout(timer);
  }, [categoryExpenses, expenses, syncContainerHeights]);

  // Sync heights on window resize
  useEffect(() => {
    const handleResize = () => {
      syncContainerHeights();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [syncContainerHeights]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const handleDatePickerOpen = (event: React.MouseEvent) => {
    setTempMonth(selectedDate.getMonth());
    setTempYear(selectedDate.getFullYear());
    setShowDatePicker(true);
  };

  const handleDatePickerApply = () => {
    const newDate = new Date(tempYear, tempMonth, 1);
    setSelectedDate(newDate);
    setShowDatePicker(false);
  };

  const handleDatePickerCancel = () => {
    setShowDatePicker(false);
  };

  const goToPreviousMonth = () => {
    setSelectedDate(prev => subMonths(prev, 1));
  };

  const goToNextMonth = () => {
    setSelectedDate(prev => addMonths(prev, 1));
  };


  return (
    <div className="app">
      <div className="container">
        {/* Dashboard Header */}
        <div className="dashboard-header">
          <h1 className="dashboard-title">SayExpense</h1>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
            {user && (
              <span style={{ marginRight: 'var(--spacing-sm)', color: 'var(--text-secondary)', fontSize: '14px' }}>
                {user.username || user.email}
              </span>
            )}
            <button
              onClick={handleRefresh}
              className="btn btn-icon btn-secondary"
              disabled={expensesLoading || categoryExpensesLoading}
              title="Refresh data"
            >
              <RefreshCw size={18} className={expensesLoading || categoryExpensesLoading ? 'spin' : ''} />
            </button>
            <button
              onClick={() => setIsSettingsModalOpen(true)}
              className="btn btn-icon btn-secondary"
              title="Account settings"
            >
              <Settings size={18} />
            </button>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="btn btn-icon btn-secondary"
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>


        {/* Charts Container */}
        <div className="charts-container">
          <div className="category-expenses-block">
            {/* Category Expenses */}
            <div ref={categoryWheelRef} className="category-wheel-container">
              <div className="chart-title-with-controls">
                <h2 className="chart-title">Expenses by Category</h2>

                {/* Month Navigation and Currency Controls */}
                <div className="chart-controls-inline">
                  {/* Month Navigation - Single Element with Date Picker */}
                  <div style={{ position: 'relative' }}>
                    <div className="month-navigation-single">
                      <button
                        onClick={goToPreviousMonth}
                        className="month-nav-btn"
                        title="Previous month"
                      >
                        <ChevronLeft size={16} />
                      </button>

                      <div className="current-month-center" onClick={handleDatePickerOpen}>
                        <Calendar size={14} />
                        <span className="month-text-inline">
                          {format(selectedDate, 'MMM yyyy')}
                        </span>
                      </div>

                      <button
                        onClick={goToNextMonth}
                        className="month-nav-btn"
                        title="Next month"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>

                    {/* Date Picker Dropdown attached to month navigation */}
                    {showDatePicker && (
                      <>
                        <div className="datepicker-backdrop" onClick={handleDatePickerCancel}></div>
                        <div className="datepicker-popup-attached">
                          <div className="datepicker-header">
                            <div className="datepicker-selects">
                              <select
                                className="datepicker-select"
                                value={tempMonth}
                                onChange={(e) => setTempMonth(parseInt(e.target.value))}
                              >
                                {monthNames.map((month, index) => (
                                  <option key={month} value={index}>
                                    {month}
                                  </option>
                                ))}
                              </select>

                              <select
                                className="datepicker-select"
                                value={tempYear}
                                onChange={(e) => setTempYear(parseInt(e.target.value))}
                              >
                                {yearOptions.map((year) => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="datepicker-footer">
                            <button
                              type="button"
                              onClick={() => {
                                const today = new Date();
                                setTempMonth(today.getMonth());
                                setTempYear(today.getFullYear());
                                setSelectedDate(today);
                                setShowDatePicker(false);
                              }}
                              className="datepicker-today-btn"
                            >
                              Today
                            </button>
                            <div className="datepicker-actions">
                              <button
                                type="button"
                                onClick={handleDatePickerCancel}
                                className="datepicker-btn datepicker-btn-cancel"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleDatePickerApply}
                                className="datepicker-btn datepicker-btn-apply"
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Currency Selector */}
                  <div className="currency-selector-inline">
                    <CurrencySelect
                      value={currency}
                      onChange={setCurrency}
                      className="filter-select filter-select-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="chart-wheel">
                {categoryExpensesLoading ? (
                  <PieChartSkeleton />
                ) : categoryExpenses ? (
                  <ExpensesByCategoryChart categoryData={categoryExpenses} selectedMonth={selectedDate} loading={categoryExpensesLoading} />
                ) : (
                  <div className="no-data">No data available</div>
                )}
              </div>
              <div className="category-list-container">
                {categoryExpensesLoading ? (
                  <CategoryListSkeleton />
                ) : categoryExpenses ? (
                  <ExpensesByCategoryBreakdown categoryData={categoryExpenses} loading={categoryExpensesLoading} />
                ) : (
                  <div className="no-data">No data available</div>
                )}
              </div>
            </div>

            {/* Monthly Expenses */}
            <div ref={monthlyExpensesRef} className="monthly-expenses-container">
              <div className="expense-list-header">
                <h2 className="expense-list-title">Monthly Expenses List</h2>
              </div>
              <div ref={expensesListRef} className="expenses-list">
                <ExpenseList
                  expenses={expenses}
                  warnings={warnings}
                  onDeleteExpense={handleDeleteExpense}
                  onEditExpense={handleEditExpense}
                  selectedMonth={selectedDate}
                  targetCurrency={currency}
                  loading={expensesLoading}
                  categories={categories}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Expenses Chart */}
        <MonthlyExpensesChart
          key={`monthly-chart-${chartsRefreshKey}`}
          currency={currency}
          startDate={monthlyChartStartDate}
          endDate={monthlyChartEndDate}
          onDateRangeChange={handleMonthlyChartDateRangeChange}
        />

        {/* Category Charts Grid */}
        <CategoryChartsGrid
          key={`category-charts-${chartsRefreshKey}`}
          currency={currency}
          startDate={categoryTrendsStartDate}
          endDate={categoryTrendsEndDate}
          onDateRangeChange={handleCategoryTrendsDateRangeChange}
        />

        {/* Floating Action Button */}
        <button
          onClick={() => {
            setCreateModalMode('expense');
            setIsCreateModalOpen(true);
          }}
          className="fab"
          title="Add new"
        >
          <Plus size={24} />
        </button>

        {/* Unified Create Modal with Tabs */}
        <UnifiedCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmitExpense={handleCreateExpense}
          onSubmitCategory={handleCreateCategory}
          loadingExpense={creatingExpense}
          loadingCategory={creatingCategory}
          categories={categories}
          currencies={currencies}
          categoriesLoading={categoriesLoading}
          initialMode={createModalMode}
        />

        {/* Edit Expense Modal */}
        {editingExpense && (
          <EditExpenseModal
            isOpen={isEditModalOpen}
            expense={editingExpense}
            onClose={() => {
              setIsEditModalOpen(false);
              setEditingExpense(null);
            }}
            onSubmit={handleUpdateExpense}
            loading={creatingExpense}
            categories={categories}
            currencies={currencies}
            categoriesLoading={categoriesLoading}
          />
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          expense={deletingExpense}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          loading={isDeleting}
        />

        {/* Account Settings Modal */}
        <AccountSettingsModal
          isOpen={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
        />

      </div>

      {/* Additional styles for animations */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }

        .text-error {
          color: var(--error);
          font-size: 12px;
          margin-top: var(--spacing-xs);
        }

        .form-input.error,
        .form-select.error {
          border-color: var(--error);
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
        }
      `}</style>
    </div>
  );
}

export default Dashboard;
